import * as ReduxToolkit from "@reduxjs/toolkit";
import { ShiftReportsList, TripsReportsRow } from "../../api";
import { BreakReportsRow } from "../../api/models/BreakReportsRow";
import { GoCancelsRow } from "../../api/models/GoCancelsRow";
import { yesterday } from "../../utils/time";

export interface TableSortState {
    lastSortedColumn: string;
    sortDirection: string;
}

export interface State {
    startDate: string;
    endDate?: string;
    driverIds: number[];
    officeId?: number;
    needGuidanceOnly: boolean;
    shiftReports: ShiftReportsList | null;
    tableSortState: TableSortState;
}

export interface TripsState {
    driverId: number;
    driverCode: string;
    shiftDate: string;
    driverName: string;
    shiftMin: string;
    naviUsageRate: number;
    needGuidanceHermesHeader: string;
    trips?: TripsReportsRow[] | null;
    goCancels?: GoCancelsRow[] | null;
    selectedTrip?: TripsReportsRow | null;
    tableSortState: TableSortState;
}

export interface BreaksState {
    officeName: string;
    driverId: number;
    shiftDate: string;
    driverName: string;
    shiftMin: string;
    breaksMin: string;
    needGuidance: string;
    breaks?: BreakReportsRow[] | null;
    tableSortState: TableSortState;
}

export function initTableSortState(tableType: string): TableSortState {
    let initialSortColumn = "";
    if (tableType === "shifts") {
        initialSortColumn = "shift_date";
    }
    if (tableType === "breaks") {
        initialSortColumn = "break_length";
    }
    if (tableType === "trips") {
        initialSortColumn = "shift_length";
    }
    return {
        lastSortedColumn: initialSortColumn,
        sortDirection: "desc",
    } as TableSortState;
}

export function initState(): State {
    return {
        startDate: yesterday(),
        endDate: "",
        needGuidanceOnly: false,
        shiftReports: null,
        officeId: -1,
        driverIds: [],
        tableSortState: initTableSortState("shifts"),
    };
}

export function initTripState(): TripsState {
    return {
        driverId: -1,
        driverCode: "-1",
        shiftDate: "",
        driverName: "",
        shiftMin: "",
        naviUsageRate: -1,
        needGuidanceHermesHeader: "",
        selectedTrip: null,
        tableSortState: initTableSortState("trips"),
    };
}

export function initBreaksState(): BreaksState {
    return {
        officeName: "",
        driverId: -1,
        shiftDate: "",
        driverName: "",
        shiftMin: "",
        breaksMin: "",
        needGuidance: "",
        tableSortState: initTableSortState("breaks"),
    };
}

export const shiftMonitoringSlice = ReduxToolkit.createSlice({
    name: "shiftMonitoring",
    initialState: initState(),
    reducers: {
        resetState: (state) => {
            state = initState();
            return state;
        },
        update_table_sort: (
            state,
            action: ReduxToolkit.PayloadAction<TableSortState | null>
        ) => {
            if (!action.payload) {
                state.tableSortState = initTableSortState("shifts");
                return state;
            }
            state.tableSortState = action.payload;
            return state;
        },
        update_shift_reports: (
            state,
            action: ReduxToolkit.PayloadAction<ShiftReportsList | null>
        ) => {
            state.shiftReports = action.payload;
            return state;
        },
        update_driver_ids: (
            state,
            action: ReduxToolkit.PayloadAction<number[]>
        ) => {
            state.driverIds = action.payload;
            return state;
        },
        update_start_date: (
            state,
            action: ReduxToolkit.PayloadAction<string>
        ) => {
            state.startDate = action.payload;
            return state;
        },
        update_need_guidance_only: (
            state,
            action: ReduxToolkit.PayloadAction<boolean>
        ) => {
            state.needGuidanceOnly = action.payload;
            return state;
        },
        update_end_date: (
            state,
            action: ReduxToolkit.PayloadAction<string>
        ) => {
            state.endDate = action.payload;
            return state;
        },
        update_office_id: (
            state,
            action: ReduxToolkit.PayloadAction<number | undefined>
        ) => {
            state.officeId = action.payload;
            return state;
        },
    },
});

export const tripsSlice = ReduxToolkit.createSlice({
    name: "trips",
    initialState: initTripState(),
    reducers: {
        resetState: (state) => {
            state = initTripState();
            return state;
        },
        update_table_sort: (
            state,
            action: ReduxToolkit.PayloadAction<TableSortState | null>
        ) => {
            if (!action.payload) {
                state.tableSortState = initTableSortState("trips");
                return state;
            }
            state.tableSortState = action.payload;
            return state;
        },
        update_selected_trip: (
            state,
            action: ReduxToolkit.PayloadAction<TripsReportsRow | null>
        ) => {
            state.selectedTrip = action.payload;
            return state;
        },
        update_selection_state: (
            state,
            action: ReduxToolkit.PayloadAction<TripsState>
        ) => {
            state.driverId = action.payload.driverId;
            state.driverCode = action.payload.driverCode;
            state.shiftDate = action.payload.shiftDate;
            state.driverName = action.payload.driverName;
            state.shiftMin = action.payload.shiftMin;
            state.naviUsageRate = action.payload.naviUsageRate;
            state.needGuidanceHermesHeader =
                action.payload.needGuidanceHermesHeader;
            return state;
        },
        update_trips: (state, action: ReduxToolkit.PayloadAction<any>) => {
            state.trips = action.payload;
            return state;
        },
        update_go_cancels: (state, action: ReduxToolkit.PayloadAction<any>) => {
            state.goCancels = action.payload;
            return state;
        },
    },
});

export const breaksSlice = ReduxToolkit.createSlice({
    name: "breaks",
    initialState: initBreaksState(),
    reducers: {
        resetState: (state) => {
            state = initBreaksState();
            return state;
        },
        update_table_sort: (
            state,
            action: ReduxToolkit.PayloadAction<TableSortState | null>
        ) => {
            if (!action.payload) {
                state.tableSortState = initTableSortState("breaks");
                return state;
            }
            state.tableSortState = action.payload;
            return state;
        },
        update_driver_id: (
            state,
            action: ReduxToolkit.PayloadAction<number>
        ) => {
            state.driverId = action.payload;
            return state;
        },
        update_selection_state: (
            state,
            action: ReduxToolkit.PayloadAction<BreaksState>
        ) => {
            state.officeName = action.payload.officeName;
            state.driverId = action.payload.driverId;
            state.shiftDate = action.payload.shiftDate;
            state.driverName = action.payload.driverName;
            state.shiftMin = action.payload.shiftMin;
            state.breaksMin = action.payload.breaksMin;
            state.needGuidance = action.payload.needGuidance;

            return state;
        },
        update_breaks: (state, action: ReduxToolkit.PayloadAction<any>) => {
            state.breaks = action.payload;
            return state;
        },
    },
});
